import type { ControlDefinition, InstrumentDefinition, PortDefinition } from "../domain/instrument";
import {
  compileSynthDocument,
  type CompiledNode,
  type CompiledSynth,
} from "../synth-designer/compiler";
import { ComponentRegistry } from "../synth-designer/component-registry";
import { createCoreComponentRegistry } from "../synth-designer/core-components";
import type {
  ControlEntity,
  PortEntity,
  SignalNodeEntity,
  SynthDocument,
} from "../synth-designer/document";
import { localFrameBounds, unionRects, type Rect } from "../synth-designer/geometry";
import type { GraphicGroup } from "../synth-designer/graphic-tree";
import {
  publishedDesignStorageKey,
  readPublishedDesign,
  type DesignDocumentStorage,
} from "../synth-designer/publication";

export const PATCH_UNITS_PER_MILLIMETRE = 4;

export type CompiledInstrumentRuntime = {
  compiled: CompiledSynth;
  definition: InstrumentDefinition;
  documentSource: "built-in" | "published";
  scale: number;
  warning?: string;
};

const runtimeRegistry = createCoreComponentRegistry();

type RuntimeCacheEntry = {
  builtInDocument: SynthDocument;
  publicationSource: string | null;
  runtime: CompiledInstrumentRuntime;
};

let storageRuntimeCaches = new WeakMap<object, Map<string, RuntimeCacheEntry>>();
let storageFreeRuntimeCache = new Map<string, RuntimeCacheEntry>();

function browserStorage(): DesignDocumentStorage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function publicationSource(
  definitionId: string,
  storage: DesignDocumentStorage | undefined,
): string | null {
  try {
    return storage?.getItem(publishedDesignStorageKey(definitionId)) ?? null;
  } catch {
    return null;
  }
}

function runtimeCache(storage: DesignDocumentStorage | undefined) {
  if (!storage) return storageFreeRuntimeCache;
  const key = storage as object;
  const existing = storageRuntimeCaches.get(key);
  if (existing) return existing;
  const created = new Map<string, RuntimeCacheEntry>();
  storageRuntimeCaches.set(key, created);
  return created;
}

function scaledRect(rect: Rect, scale = PATCH_UNITS_PER_MILLIMETRE): Rect {
  return {
    x: rect.x * scale,
    y: rect.y * scale,
    width: rect.width * scale,
    height: rect.height * scale,
  };
}

function boundNode(compiled: CompiledSynth, kind: "control" | "port", id: string) {
  const binding = compiled.semantics[kind === "control" ? "controls" : "ports"].get(id);
  const nodeId = binding?.nodeIds[0];
  return nodeId ? compiled.nodeIndex.get(nodeId) : undefined;
}

function legacySignalType(signalType: PortEntity["signalType"]): PortDefinition["signalType"] {
  return signalType === "midi" || signalType === "power" ? "unknown" : signalType;
}

function semanticSectionId(compiled: CompiledSynth, node: CompiledNode) {
  let current: CompiledNode | undefined = node;
  while (current) {
    if (current.source.kind === "surface" && current.parentId && current.id !== "nameplate") {
      return current.id.startsWith("section:") ? current.id.slice("section:".length) : current.id;
    }
    current = current.parentId ? compiled.nodeIndex.get(current.parentId) : undefined;
  }
  return compiled.scene.artboards.get(node.artboardId ?? "")?.source.kind === "rear"
    ? "rear-panel"
    : "front-panel";
}

function panelTone(colour: string, fallback: "light" | "dark" = "dark") {
  const match = /^#([0-9a-f]{6})$/i.exec(colour);
  if (!match) return fallback;
  const numeric = Number.parseInt(match[1]!, 16);
  const red = (numeric >> 16) & 0xff;
  const green = (numeric >> 8) & 0xff;
  const blue = numeric & 0xff;
  return red * 0.299 + green * 0.587 + blue * 0.114 >= 145 ? "light" : "dark";
}

function compiledSections(
  compiled: CompiledSynth,
  scale: number,
): InstrumentDefinition["sections"] {
  return [...compiled.nodeIndex.values()].flatMap((node) => {
    if (node.source.kind !== "surface" || !node.parentId || node.id === "nameplate") return [];
    const id = node.id.startsWith("section:") ? node.id.slice("section:".length) : node.id;
    const colour =
      typeof node.resolvedStyle.fill === "string" ? node.resolvedStyle.fill : "#181c1c";
    const rear = compiled.scene.artboards.get(node.artboardId ?? "")?.source.kind === "rear";
    const authoredKind = node.resolvedStyle.sectionKind;
    const kind =
      authoredKind === "panel" ||
      authoredKind === "performance" ||
      authoredKind === "inset" ||
      authoredKind === "rear" ||
      authoredKind === "rear-inset"
        ? authoredKind
        : undefined;
    return [
      {
        id,
        label: node.source.name,
        shortLabel:
          typeof node.resolvedStyle.sectionShortLabel === "string"
            ? node.resolvedStyle.sectionShortLabel
            : node.source.name,
        ...scaledRect(node.layoutBounds, scale),
        colour,
        tone: panelTone(colour),
        kind: kind ?? (rear ? "rear" : "panel"),
      },
    ];
  });
}

function authoredControlType(node: CompiledNode, entity: ControlEntity): ControlDefinition["type"] {
  const componentType = node.source.component?.type;
  if (entity.type === "continuous") {
    if (componentType === "core.slider") return "slider";
    if (componentType === "core.wheel") return "wheel";
    return "knob";
  }
  if (entity.type === "discrete") {
    return componentType === "core.rotary-selector" ? "rotary-selector" : "lever";
  }
  return componentType === "core.button" ? "button" : "switch";
}

function authoredControl(
  compiled: CompiledSynth,
  node: CompiledNode,
  entity: ControlEntity,
): ControlDefinition {
  const variant = node.source.component?.variant;
  const props = node.source.component?.props;
  return {
    id: entity.id,
    sectionId: semanticSectionId(compiled, node),
    label: entity.label,
    shortLabel: entity.shortLabel ?? entity.label,
    type: authoredControlType(node, entity),
    position: { x: 0, y: 0 },
    size: 1,
    defaultValue: 0,
    display: typeof props?.display === "string" ? props.display : undefined,
    accent: typeof props?.accent === "string" ? props.accent : undefined,
    appearance: variant === "candy" || variant === "lamp" ? variant : undefined,
  };
}

function authoredPort(
  compiled: CompiledSynth,
  node: CompiledNode,
  entity: PortEntity,
): PortDefinition {
  return {
    id: entity.id,
    sectionId: semanticSectionId(compiled, node),
    label: entity.label,
    shortLabel: entity.shortLabel ?? entity.label,
    position: { x: 0, y: 0 },
    direction: entity.direction,
    signalType: legacySignalType(entity.signalType),
    location:
      compiled.scene.artboards.get(node.artboardId ?? "")?.source.kind === "rear"
        ? "rear"
        : "front",
    normalledFrom: entity.normalledFrom,
    description: entity.description,
    electrical: entity.electrical,
    size: 1,
  };
}

function authoredSignalNode(
  compiled: CompiledSynth,
  node: CompiledNode | undefined,
  entity: SignalNodeEntity,
) {
  return {
    id: entity.id,
    sectionId: node ? semanticSectionId(compiled, node) : (entity.sectionId ?? "front-panel"),
    label: entity.label,
    shortLabel: entity.shortLabel ?? entity.label,
    position: { x: 0, y: 0 },
    signalType: legacySignalType(entity.signalType),
    kind: entity.kind,
  };
}

function semanticEndpointLabel(compiled: CompiledSynth, endpointId: string) {
  return (
    compiled.semantics.ports.get(endpointId)?.entity.label ??
    compiled.semantics.signalNodes.get(endpointId)?.entity.label ??
    endpointId
  );
}

function controlGeometry(
  control: ControlDefinition,
  entity: ControlEntity | undefined,
  compiled: CompiledSynth,
  scale: number,
): ControlDefinition {
  const node = boundNode(compiled, "control", control.id);
  const anchor = node?.anchors.primary;
  if (!node || !anchor) return control;
  const bounds = scaledRect(node.layoutBounds, scale);
  const size =
    control.type === "slider" || control.type === "wheel"
      ? bounds.height
      : Math.min(bounds.width, bounds.height);
  return {
    ...control,
    label: entity?.label ?? control.label,
    shortLabel: entity?.shortLabel ?? entity?.label ?? control.shortLabel,
    defaultValue: entity
      ? (entity.defaultValue - entity.minimum) / (entity.maximum - entity.minimum)
      : control.defaultValue,
    options: entity?.options
      ? entity.options.map((option) => ({
          id: option.id,
          label: option.label,
          value: (option.value - entity.minimum) / (entity.maximum - entity.minimum),
          legend: control.options?.find(({ id }) => id === option.id)?.legend,
        }))
      : control.options,
    position: { x: anchor.x * scale, y: anchor.y * scale },
    size: Math.max(1, size),
    width: Math.max(1, bounds.width),
    height: Math.max(1, bounds.height),
  };
}

function portGeometry(
  port: PortDefinition,
  entity: PortEntity | undefined,
  compiled: CompiledSynth,
  scale: number,
): PortDefinition {
  const anchor = compiled.portAnchors.get(port.id);
  const node = boundNode(compiled, "port", port.id);
  if (!anchor) return port;
  return {
    ...port,
    label: entity?.label ?? port.label,
    shortLabel: entity?.shortLabel ?? entity?.label ?? port.shortLabel,
    direction: entity?.direction ?? port.direction,
    signalType: entity ? legacySignalType(entity.signalType) : port.signalType,
    normalledFrom: entity?.normalledFrom,
    description: entity?.description ?? port.description,
    electrical: entity?.electrical ?? port.electrical,
    position: { x: anchor.x * scale, y: anchor.y * scale },
    size: node
      ? Math.max(1, Math.min(node.layoutBounds.width, node.layoutBounds.height) * scale)
      : port.size,
  };
}

export function projectCompiledInstrumentDefinition(
  compiled: CompiledSynth,
  scale = PATCH_UNITS_PER_MILLIMETRE,
): InstrumentDefinition {
  const visibleArtboards = [...compiled.scene.artboards.values()].filter(
    ({ source }) => source.visible,
  );
  const documentBounds = scaledRect(
    unionRects(visibleArtboards.map(({ bounds }) => bounds)) ?? {
      x: 0,
      y: 0,
      width: 1,
      height: 1,
    },
    scale,
  );
  const front = compiled.scene.artboards.get("front");
  const rear = compiled.scene.artboards.get("rear");
  const frontBounds = front
    ? scaledRect(front.bounds, scale)
    : { x: 0, y: 0, width: Math.max(1, documentBounds.width), height: 1 };
  const rearBounds = rear ? scaledRect(rear.bounds, scale) : undefined;
  const nameplate = compiled.nodeIndex.get("nameplate");
  const nameplateColour =
    nameplate && typeof nameplate.resolvedStyle.fill === "string"
      ? nameplate.resolvedStyle.fill
      : "#74bed0";

  return {
    schemaVersion: 1,
    definitionId: compiled.document.id,
    manufacturer: compiled.document.metadata.manufacturer ?? "Unknown manufacturer",
    model: compiled.document.metadata.model,
    displayName: compiled.document.metadata.displayName,
    panel: {
      width: Math.max(1, documentBounds.x + documentBounds.width),
      height: Math.max(1, documentBounds.y + documentBounds.height),
      mainBounds: frontBounds,
      rearBounds,
      nameplate: nameplate
        ? {
            ...scaledRect(nameplate.layoutBounds, scale),
            colour: nameplateColour,
          }
        : {
            x: frontBounds.x,
            y: frontBounds.y + Math.max(0, frontBounds.height - 1),
            width: Math.max(1, frontBounds.width),
            height: 1,
            colour: nameplateColour,
          },
    },
    sections: compiledSections(compiled, scale),
    controls: [...compiled.semantics.controls.values()].flatMap(({ entity, nodeIds }) => {
      const node = nodeIds[0] ? compiled.nodeIndex.get(nodeIds[0]) : undefined;
      if (!node) return [];
      return [controlGeometry(authoredControl(compiled, node, entity), entity, compiled, scale)];
    }),
    ports: [...compiled.semantics.ports.values()].flatMap(({ entity, nodeIds }) => {
      const node = nodeIds[0] ? compiled.nodeIndex.get(nodeIds[0]) : undefined;
      if (!node || !compiled.portAnchors.has(entity.id)) return [];
      return [portGeometry(authoredPort(compiled, node, entity), entity, compiled, scale)];
    }),
    signalNodes: [...compiled.semantics.signalNodes.values()].flatMap(({ entity, nodeIds }) => {
      const node = nodeIds[0] ? compiled.nodeIndex.get(nodeIds[0]) : undefined;
      const anchor = compiled.signalNodeAnchors.get(entity.id);
      return [
        {
          ...authoredSignalNode(compiled, node, entity),
          label: entity.label,
          shortLabel: entity.shortLabel ?? entity.label,
          signalType: legacySignalType(entity.signalType),
          kind: entity.kind,
          position: anchor
            ? { x: anchor.x * scale, y: anchor.y * scale }
            : entity.position
              ? { x: entity.position.x * scale, y: entity.position.y * scale }
              : { x: 0, y: 0 },
        },
      ];
    }),
    normalledConnections: compiled.semantics.connections
      .filter(({ kind }) => kind === "normalled")
      .map((connection) => ({
        id: connection.id,
        fromId: connection.fromId,
        toId: connection.toId,
        breakCondition: { kind: connection.breakCondition },
        signalType: legacySignalType(connection.signalType),
        label:
          connection.label ??
          `${semanticEndpointLabel(compiled, connection.fromId)} to ${semanticEndpointLabel(compiled, connection.toId)}`,
        description:
          connection.description ?? "Normalled signal route defined by the synth design.",
        activeWhen: connection.activeWhen,
      })),
    signalConnections: compiled.semantics.connections
      .filter(({ kind }) => kind === "internal")
      .map((connection) => ({
        id: connection.id,
        fromId: connection.fromId,
        toId: connection.toId,
        signalType: legacySignalType(connection.signalType),
        label:
          connection.label ??
          `${semanticEndpointLabel(compiled, connection.fromId)} to ${semanticEndpointLabel(compiled, connection.toId)}`,
        description: connection.description ?? "Internal signal route defined by the synth design.",
        bidirectional: connection.bidirectional,
      })),
  };
}

export function createCompiledInstrumentRuntime(
  builtInDocument: SynthDocument,
  storage: DesignDocumentStorage | undefined = browserStorage(),
): CompiledInstrumentRuntime {
  const published = readPublishedDesign(builtInDocument.id, storage);
  const publishedSnapshot =
    published.snapshot &&
    published.snapshot.document.componentLibrary.package ===
      builtInDocument.componentLibrary.package &&
    published.snapshot.document.componentLibrary.version >= builtInDocument.componentLibrary.version
      ? published.snapshot
      : undefined;
  let document = publishedSnapshot?.document ?? builtInDocument;
  let compiled = compileSynthDocument(document, { registry: runtimeRegistry });
  let documentSource: CompiledInstrumentRuntime["documentSource"] = publishedSnapshot
    ? "published"
    : "built-in";
  let warning =
    published.snapshot && !publishedSnapshot
      ? `The published ${builtInDocument.metadata.model} layout used an older built-in layout generation, so Patch mode loaded the updated layout.`
      : published.warning;

  if (!compiled.valid && documentSource === "published") {
    document = builtInDocument;
    compiled = compileSynthDocument(document, { registry: runtimeRegistry });
    documentSource = "built-in";
    warning = "The published design did not compile, so Patch mode restored the built-in layout.";
  }

  return {
    compiled,
    definition: projectCompiledInstrumentDefinition(compiled),
    documentSource,
    scale: PATCH_UNITS_PER_MILLIMETRE,
    warning,
  };
}

export function cachedCompiledInstrumentRuntime(
  builtInDocument: SynthDocument,
  storage: DesignDocumentStorage | undefined = browserStorage(),
): CompiledInstrumentRuntime {
  const cache = runtimeCache(storage);
  const source = publicationSource(builtInDocument.id, storage);
  const cached = cache.get(builtInDocument.id);
  if (cached?.builtInDocument === builtInDocument && cached.publicationSource === source) {
    return cached.runtime;
  }
  const runtime = createCompiledInstrumentRuntime(builtInDocument, storage);
  cache.set(builtInDocument.id, {
    builtInDocument,
    publicationSource: source,
    runtime,
  });
  return runtime;
}

export function clearCompiledInstrumentRuntimeCache() {
  storageRuntimeCaches = new WeakMap();
  storageFreeRuntimeCache = new Map();
}

function compileLiveNode(
  compiled: CompiledSynth,
  node: CompiledNode,
  value: number,
  registry: ComponentRegistry,
): GraphicGroup | null {
  const source = node.source;
  if (source.kind !== "component" || !source.component) return null;
  const component = registry.get(source.component.type);
  if (!component) return null;
  const parsedProps = component.propertySchema.safeParse({
    ...source.component.props,
    defaultValue: value,
  });
  if (!parsedProps.success) return null;
  const result = component.compile({
    node: source,
    frame: localFrameBounds(source.frame),
    props: parsedProps.data,
    variant: source.component.variant,
    style: node.resolvedStyle,
    tokens: compiled.resolvedTokens,
    target: "screen",
  });
  const children = node.childIds
    .map((childId) => compiled.nodeIndex.get(childId)?.graphic)
    .filter((child): child is GraphicGroup => child !== undefined);
  return {
    ...node.graphic,
    children: [...result.graphics, ...children],
  };
}

export function compileLiveControlGraphics(
  runtime: CompiledInstrumentRuntime,
  controlId: string,
  value: number,
) {
  const binding = runtime.compiled.semantics.controls.get(controlId);
  if (!binding) return [];
  return binding.nodeIds.flatMap((nodeId) => {
    const node = runtime.compiled.nodeIndex.get(nodeId);
    if (!node) return [];
    const graphic = compileLiveNode(runtime.compiled, node, value, runtimeRegistry);
    return graphic ? [{ nodeId, graphic }] : [];
  });
}

export function compileInstrumentForPrint(
  runtime: CompiledInstrumentRuntime,
  controlValues: Readonly<Record<string, number>>,
) {
  const document = structuredClone(runtime.compiled.document);
  for (const node of Object.values(document.nodes)) {
    if (
      node.kind !== "component" ||
      !node.component ||
      node.binding?.kind !== "control" ||
      !("defaultValue" in node.component.props)
    ) {
      continue;
    }
    const entity = document.semantics.controls[node.binding.entityId];
    if (!entity) continue;
    const span = entity.maximum - entity.minimum;
    const fallback = span === 0 ? 0 : (entity.defaultValue - entity.minimum) / span;
    node.component.props.defaultValue = controlValues[entity.id] ?? fallback;
  }
  return compileSynthDocument(document, { registry: runtimeRegistry, target: "print" });
}
